import { Box, Divider, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";
import {
  ShipmentTabs,
  ShipmentsTable,
  AdvancedSettingDrawer,
  BundleViewSwitch,
  PrintByItemWrapper,
  AutoPrintSwitch,
} from "../components";
import { useEffect, useState } from "react";
import {
  Filter,
  IS_AUTO_PRINT,
  IS_BUNDLE_VIEW,
  ORDER_SHIPMENT_SEARCH_OPTIONS_LS,
  SHIPMENTS_TAB,
  SHIPMENTS_TABLE_TABS,
} from "../constants";
import { CustomSnackBar } from "../components/Snackbar";
import { getShipmentSearchFilters } from "../utils";
import { ISearchOrderShipmentsErrorResponse } from "../interfaces/ISearchOrderShipmentsOptions";

export const ShipmentsModule = () => {
  const [openAdvancedSettings, setOpenAdvancedSettings] = useState(false);
  const [snackBar, setSnackBar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const [searchOrderShipmentsResponse, setSearchOrderShipmentsResponse] = useState<any>();
  const [errorShipmentOrderResponse, setErrorShipmentOrderResponse] = useState<ISearchOrderShipmentsErrorResponse>({});

  const [bundleView, setBundleView] = useState<boolean>(JSON.parse(localStorage.getItem(IS_BUNDLE_VIEW)) || false);
  const [autoPrint, setAutoPrint] = useState<boolean>(JSON.parse(localStorage.getItem(IS_AUTO_PRINT)) || false);
  const [activeShipmentTabId, setActiveShipmentTabId] = useState(
    localStorage.getItem(SHIPMENTS_TAB) || SHIPMENTS_TABLE_TABS.ALL_PICKED_ORDERS
  );

  const [searchFilters, setSearchFilters] = useState<Filter[]>(getShipmentSearchFilters(activeShipmentTabId));
  const [searchSortOption, setSearchSortOption] = useState<{ direction: "asc" | "desc"; field: string } | undefined>({
    direction: "desc",
    field: "appImportDateTime",
  });
  const [searchOrderShipmentsText, setSearchOrderShipmentsText] = useState<string>("");
  const [orderShipmentsSearchType, setOrderShipmentsSearchType] = useState("PRODUCT");

  useEffect(() => {
    localStorage.setItem(
      ORDER_SHIPMENT_SEARCH_OPTIONS_LS,
      JSON.stringify({ searchOptions: { filters: searchFilters, sortOptions: searchSortOption } })
    );
  }, [searchFilters, searchSortOption, activeShipmentTabId]);

  useEffect(() => {
    // Set default active tab on component mount
    !activeShipmentTabId && localStorage.setItem(SHIPMENTS_TAB, SHIPMENTS_TABLE_TABS.ALL_PICKED_ORDERS.value);
  }, []);

  // Centralized Bundle View and Auto Print storage
  useEffect(() => {
    localStorage.setItem(IS_BUNDLE_VIEW, JSON.stringify(bundleView));
  }, [bundleView]);

  useEffect(() => {
    localStorage.setItem(IS_AUTO_PRINT, JSON.stringify(autoPrint));
  }, [autoPrint]);

  // Custom search logic
  const onCustomOrderShipmentsSearch = (customSearchText, searchType) => {
    setSearchOrderShipmentsText(customSearchText);
    setOrderShipmentsSearchType(searchType);
  };

  // trigger on tab value change in orders page
  const onOrdersPageTabValueChange = (shipmentTabId) => {
    localStorage.removeItem(ORDER_SHIPMENT_SEARCH_OPTIONS_LS);
    setActiveShipmentTabId(shipmentTabId);
  };

  const handleAdvancedSettingsClose = ({ sortByField, sortType, filters }) => {
    // Note - need to joing preset filter values as api is requiring it as a comma separated string rather than array of strings
    setSearchFilters(filters);
    sortByField && sortType && setSearchSortOption({ direction: sortType, field: sortByField });
    setOpenAdvancedSettings((prevOnOpenTableDetails) => !prevOnOpenTableDetails);
  };

  useEffect(() => {
    if (searchOrderShipmentsResponse?.success === true) {
      setSnackBar({
        open: true,
        message: searchOrderShipmentsResponse?.message,
        severity: "success",
      });
    }
  }, [searchOrderShipmentsResponse]);

  useEffect(() => {
    if (searchOrderShipmentsResponse?.success === false) {
      setSnackBar({
        open: true,
        message: errorShipmentOrderResponse?.error?.message,
        severity: "error",
      });
    }
  }, [errorShipmentOrderResponse, searchSortOption, searchFilters]);

  useEffect(() => {
    setSearchFilters(getShipmentSearchFilters(activeShipmentTabId));
  }, [activeShipmentTabId]);

  return (
    <>
      <Paper sx={{ padding: "16px", overflow: "hidden" }}>
        {snackBar.open && (
          <CustomSnackBar
            open={snackBar.open}
            message={snackBar.message}
            autoHideDuration={snackBar.severity === "success" ? 800 : 1800}
            onClose={() => setSnackBar({ open: false, message: "", severity: "" })}
            severity={snackBar.severity}
          />
        )}
        <Stack direction="row" alignItems="center" justifyContent="end">
          {activeShipmentTabId === SHIPMENTS_TABLE_TABS.ALL_PICKED_ORDERS.value && (
            <AutoPrintSwitch isAutoPrint={autoPrint} onChange={setAutoPrint} />
          )}
          <BundleViewSwitch isBundleView={bundleView} onChange={setBundleView} />
        </Stack>

        {/* Shipments table tabs */}
        <ShipmentTabs
          onOrderShipmentsCustomSearch={onCustomOrderShipmentsSearch}
          onAdvancedSettingsOpen={() => setOpenAdvancedSettings(!openAdvancedSettings)}
          onShipmentTabChange={(shipmentTabId) => {
            onOrdersPageTabValueChange(shipmentTabId);
          }}
        />

        {/* Shipments table */}
        {activeShipmentTabId != SHIPMENTS_TABLE_TABS.PRINT_HUB.value && (
          <>
            <ShipmentsTable
              searchFilters={searchFilters}
              bundleView={bundleView}
              searchOrderShipmentsText={searchOrderShipmentsText}
              orderShipmentsSearchType={orderShipmentsSearchType}
              searchSortOption={searchSortOption}
              setSearchOrderShipmentsResponse={setSearchOrderShipmentsResponse}
              setErrorShipmentOrderResponse={setErrorShipmentOrderResponse}
              setSnackBar={setSnackBar}
            />
          </>
        )}
        {activeShipmentTabId === SHIPMENTS_TABLE_TABS.PRINT_HUB.value && <PrintByItemWrapper bundleView={bundleView} />}
        {/* Advanced settings drawer */}
        {openAdvancedSettings && (
          <AdvancedSettingDrawer
            open={openAdvancedSettings}
            page={"SHIPMENTS"}
            onSaveOrCancelFilters={() => setOpenAdvancedSettings(!openAdvancedSettings)}
            onSaveAndApplyFilters={({ sortByField, sortType, filters }) => {
              handleAdvancedSettingsClose({ sortByField, sortType, filters });
            }}
          />
        )}
      </Paper>
    </>
  );
};
